import React, { useState, useRef, useCallback, useMemo } from 'react'
import {
  EVENTS,
  STATUS,
  ACTIONS,
  CallBackProps,
  Step as JoyrideStepBasic,
  StoreHelpers,
} from 'react-joyride'
import { Tabs } from '../../layout/MainLayout'

import {
  TutorialStep as ImportedTutorialStep,
  ExtendedHelpers as ImportedExtendedHelpers,
} from '../../tour/types'
import {
  cleanupAllTourElements,
  cleanupDependenciesForStepTransition,
} from '../../tour/stepManagement/dependencyManager'
import { waitForElementAndScroll } from '../../tour/stepManagement/elementUtils'

interface CustomStepTargetForMapping {
  selector: string
  [key: string]: any
}

interface InternalTutorialStep extends Omit<JoyrideStepBasic, 'target' | 'content' | 'title'> {
  target: string | CustomStepTargetForMapping
  content: React.ReactNode
  title?: React.ReactNode
  isFeedback?: boolean
  hideFooter?: boolean
  onEnter?: (helpers: ImportedExtendedHelpers, index: number) => void
  onExit?: (helpers: ImportedExtendedHelpers, index: number) => void
  [key: string]: any
}

interface UseTourControllerProps {
  initialStepsFromConfig: readonly ImportedTutorialStep[]
  onComplete?: () => void
  showcaseContext?: any
  modalContext?: any
  setActiveTab?: (tab: { tab: Tabs; options?: any }) => void
}

const useTourController = ({
  initialStepsFromConfig,
  onComplete,
  showcaseContext,
  modalContext,
  setActiveTab,
}: UseTourControllerProps) => {
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [rawJoyrideHelpers, setRawJoyrideHelpers] = useState<StoreHelpers | null>(null)

  const operationInProgressRef = useRef(false)
  const jumpTargetRef = useRef<number | null>(null)

  const createCompatibleExtendedHelpers = useCallback((): ImportedExtendedHelpers | null => {
    if (!rawJoyrideHelpers) return null
    return { ...rawJoyrideHelpers } as ImportedExtendedHelpers
  }, [rawJoyrideHelpers])

  const exposedStartTour = () => {
    setStepIndex(0)
    setRun(true)
  }

  const retryMissing = async (idx: number) => {
    const currentStepConfig = initialStepsFromConfig[idx]
    const compatibleHelpers = createCompatibleExtendedHelpers()

    if (!compatibleHelpers) {
      return
    }

    if ((currentStepConfig as any)?.isFeedback && currentStepConfig?.target === 'body') {
      compatibleHelpers.go(idx)
      return
    }

    let sel: string | undefined
    if (currentStepConfig?.target) {
      if (typeof currentStepConfig.target === 'string') {
        sel = currentStepConfig.target
      } else if (
        typeof currentStepConfig.target === 'object' &&
        'selector' in currentStepConfig.target
      ) {
        sel = (currentStepConfig.target as { selector: string }).selector
      }
    }

    if (!sel || sel === 'body') {
      compatibleHelpers.go(idx)
      return
    }

    if (run && compatibleHelpers.close) compatibleHelpers.close()

    operationInProgressRef.current = true

    try {
      await waitForElementAndScroll(sel!, 2000)
      await new Promise((resolve) => setTimeout(resolve, 100))

      operationInProgressRef.current = false
      if (compatibleHelpers.open) compatibleHelpers.open()

      setTimeout(() => {
        compatibleHelpers.go(idx)
      }, 50)
    } catch (error) {
      operationInProgressRef.current = false
      if (compatibleHelpers.open) compatibleHelpers.open()

      if (compatibleHelpers.next) compatibleHelpers.next()
    }
  }

  const handleJoyrideCallback = async (data: CallBackProps) => {
    const compatibleHelpers = createCompatibleExtendedHelpers()

    const isCriticalEvent =
      data.type === EVENTS.TOUR_START ||
      data.type === EVENTS.TOUR_END ||
      data.type === EVENTS.TOUR_STATUS ||
      data.status === STATUS.READY ||
      data.status === STATUS.FINISHED ||
      data.status === STATUS.SKIPPED ||
      data.action === ACTIONS.CLOSE

    if (!compatibleHelpers && !isCriticalEvent) {
      return
    }

    if (data.type === EVENTS.TARGET_NOT_FOUND) {
      await retryMissing(data.index)
      return
    }

    const jumping = jumpTargetRef.current !== null
    if (jumping && (data.action === ACTIONS.NEXT || data.action === ACTIONS.PREV)) {
      return
    }
    if (jumping && data.action === ACTIONS.UPDATE && data.index === jumpTargetRef.current) {
      jumpTargetRef.current = null

      const jumpTargetStep = initialStepsFromConfig[data.index] as ImportedTutorialStep
      if (jumpTargetStep?.beforeShowPromise && compatibleHelpers) {
        operationInProgressRef.current = true
        setRun(false)

        try {
          await jumpTargetStep.beforeShowPromise(compatibleHelpers, data.index)
        } catch (error) {
        } finally {
          operationInProgressRef.current = false
          setStepIndex(data.index)
          setRun(true)
        }
      } else {
        setStepIndex(data.index)
      }
      return
    }

    const currentStepConfig = initialStepsFromConfig[data.index] as ImportedTutorialStep

    if (data.type === EVENTS.STEP_BEFORE && typeof data.index === 'number' && compatibleHelpers) {
      currentStepConfig?.onEnter?.(compatibleHelpers, data.index)
    }

    if (data.type === EVENTS.STEP_AFTER && typeof data.index === 'number') {
      if (compatibleHelpers) currentStepConfig?.onExit?.(compatibleHelpers, data.index)

      let nextIndexToProcess: number | null = null

      if (data.action === ACTIONS.NEXT) {
        nextIndexToProcess = data.index + 1
      } else if (data.action === ACTIONS.PREV) {
        nextIndexToProcess = data.index - 1
      } else if (data.action === ACTIONS.UPDATE) {
        setStepIndex(data.index)
      }

      if (
        nextIndexToProcess !== null &&
        nextIndexToProcess >= 0 &&
        nextIndexToProcess < initialStepsFromConfig.length
      ) {
        const targetStepConfig = initialStepsFromConfig[nextIndexToProcess] as ImportedTutorialStep

        if (compatibleHelpers) {
          try {
            await cleanupDependenciesForStepTransition(
              data.index,
              nextIndexToProcess,
              initialStepsFromConfig,
              modalContext,
              showcaseContext,
              setActiveTab
            )
          } catch (error) {
            console.error('[Joyride Debug] Error during step transition dependency cleanup:', error)
          }
        }

        if (targetStepConfig?.beforeShowPromise && compatibleHelpers) {
          operationInProgressRef.current = true
          setRun(false)

          try {
            await targetStepConfig.beforeShowPromise(compatibleHelpers, nextIndexToProcess)
          } catch (error) {
            console.warn(
              `[Joyride Debug] beforeShowPromise for step ${nextIndexToProcess} failed:`,
              error
            )
          } finally {
            operationInProgressRef.current = false
            setStepIndex(nextIndexToProcess)
            setRun(true)
          }
        } else {
          setStepIndex(nextIndexToProcess)
        }
      }
    }

    if (
      (data.status === STATUS.FINISHED ||
        data.status === STATUS.SKIPPED ||
        data.action === ACTIONS.CLOSE) &&
      !operationInProgressRef.current
    ) {
      setStepIndex(0)
      setRun(false)

      onComplete?.()

      cleanupAllTourElements({ modalContext, showcaseContext, setActiveTab }).catch(() => {})
    }
  }

  const joyrideMappedSteps = useMemo(() => {
    return initialStepsFromConfig.map((s_imported) => {
      const s = s_imported as InternalTutorialStep
      let targetValue: string
      if (typeof s.target === 'string') {
        targetValue = s.target
      } else if (
        s.target &&
        typeof (s.target as CustomStepTargetForMapping).selector === 'string'
      ) {
        targetValue = (s.target as CustomStepTargetForMapping).selector
      } else {
        targetValue = 'body'
      }
      return {
        ...s_imported,
        target: targetValue,
        content: s.content,
        title: s.title,
      } as JoyrideStepBasic
    })
  }, [initialStepsFromConfig])

  return {
    run,
    setRun,
    stepIndex,
    setStepIndex,
    joyrideSteps: joyrideMappedSteps,
    getAllOriginalSteps: initialStepsFromConfig,
    setRawJoyrideHelpers,
    handleJoyrideCallback,
    exposedStartTour,
    jumpTargetRef,
    createCompatibleExtendedHelpers,
  }
}

export default useTourController
